import { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Link,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import {
  Pause as ParkIcon,
  PlayArrow as PursueIcon,
  NoteAdd as NoteIcon,
  ArrowBack as BackIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import api from '../../api/axios';

interface Fire {
  fire_id: string;
  scan_id: string;
  rule_id: string;
  rule_version: number;
  fired_at: string;
  metric_value: string | null;
  amount_usd: string;
  analysis_json: Record<string, any>;
}

interface ExistingCase {
  case_id: string;
  case_name: string;
  case_status: string;
}

export interface Lead {
  lead_id: string;
  rule_id: string;
  rule_archetype: string;
  rule_current_version: number;
  subject_type: string;
  subject_id: string;
  display_name: string;
  amount_usd: string;
  last_fired_at: string | null;
  last_fired_rule_version: number | null;
  status: string;
  disposition_reason: string;
  case_id: string | null;
  assigned_to: string | null;
  notes: string;
  fires: Fire[];
  existing_cases_for_subject: ExistingCase[];
  created_at: string;
  updated_at: string;
}

const LeadDetail = ({ leadId }: { leadId: string }) => {
  const navigate = useNavigate();
  const [lead, setLead] = useState<Lead | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Action dialogs
  const [noteOpen, setNoteOpen] = useState(false);
  const [statusDialog, setStatusDialog] = useState<{ open: boolean; target: string }>({ open: false, target: '' });
  const [promoteOpen, setPromoteOpen] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState(false);

  const fetchLead = async () => {
    setError(null);
    try {
      const res = await api.get(`/explore/leads/${leadId}/`);
      setLead(res.data);
    } catch (err: any) {
      setError(err?.response?.data?.error || err.message || 'failed');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setLoading(true);
    setLead(null);
    fetchLead();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leadId]);

  // Keyboard shortcuts when a lead is loaded
  useEffect(() => {
    if (!lead) return;
    const onKey = (e: KeyboardEvent) => {
      // Ignore when typing in fields
      const t = e.target as HTMLElement;
      if (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA') return;
      if (e.key === 'n' || e.key === 'N') {
        e.preventDefault(); setNoteOpen(true);
      } else if (e.key === 'c' || e.key === 'C') {
        e.preventDefault(); setStatusDialog({ open: true, target: 'closed' });
      } else if (e.key === 'p' || e.key === 'P') {
        e.preventDefault(); if (!lead.case_id) setPromoteOpen(true);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [lead]);

  if (loading) {
    return <Box sx={{ p: 4, display: 'flex', justifyContent: 'center' }}><CircularProgress /></Box>;
  }
  if (error || !lead) {
    return <Box sx={{ p: 4 }}><Typography color="error">{error || 'Lead not found'}</Typography></Box>;
  }

  const amount = parseFloat(lead.amount_usd || '0');
  const stale = lead.rule_current_version && lead.last_fired_rule_version && lead.last_fired_rule_version < lead.rule_current_version;

  // Pull NPPES provider context from the most recent fire's analysis_json
  const latestFire = lead.fires?.[0];
  const provider = latestFire?.analysis_json || {};

  return (
    <Box>
      {/* Header */}
      <Box sx={{ p: 3, pb: 2, borderBottom: 1, borderColor: 'divider' }}>
        {/* Breadcrumb back to the rule that produced this lead */}
        {lead.rule_id && (
          <Box
            onClick={() => navigate(`/explore/rules/${lead.rule_id}`)}
            sx={{
              display: 'inline-flex', alignItems: 'center', gap: 0.5,
              cursor: 'pointer', mb: 1,
              color: 'text.secondary',
              '&:hover': { color: 'primary.main' },
            }}
          >
            <BackIcon sx={{ fontSize: '1rem' }} />
            <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
              {lead.rule_id}
            </Typography>
          </Box>
        )}
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2 }}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h5" sx={{ fontWeight: 600 }}>
              {lead.display_name || `${lead.subject_type}:${lead.subject_id}`}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
              {lead.subject_type}:{lead.subject_id}
            </Typography>
            <Box sx={{ display: 'flex', gap: 0.75, mt: 1, flexWrap: 'wrap' }}>
              <Chip label={lead.status} size="small" color={leadStatusColor(lead.status)} />
              <Chip
                label={lead.rule_id}
                size="small" variant="outlined"
                onClick={() => navigate(`/explore/rules/${lead.rule_id}`)}
                sx={{ cursor: 'pointer' }}
              />
              {lead.rule_archetype && <Chip label={lead.rule_archetype} size="small" variant="outlined" />}
              {stale && <Chip label="stale review" size="small" color="warning" />}
            </Box>
          </Box>
          <Box sx={{ textAlign: 'right' }}>
            <Typography variant="overline" color="text.secondary">Latest amount</Typography>
            <Typography variant="h4" sx={{ fontWeight: 600, lineHeight: 1.1 }}>
              ${amount.toLocaleString(undefined, { maximumFractionDigits: 0 })}
            </Typography>
            {lead.last_fired_at && (
              <Typography variant="caption" color="text.secondary">
                Last fired {new Date(lead.last_fired_at).toLocaleDateString()}
              </Typography>
            )}
          </Box>
        </Box>

        {/* Action footer */}
        <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
          <Button startIcon={<NoteIcon />} onClick={() => setNoteOpen(true)} size="small" sx={{ textTransform: 'none' }}>
            Note
          </Button>
          <Button
            startIcon={<ParkIcon />}
            onClick={() => setStatusDialog({ open: true, target: 'closed' })}
            disabled={lead.status === 'closed'}
            size="small" sx={{ textTransform: 'none' }}
          >
            Close
          </Button>
          {lead.case_id ? (
            <Button
              variant="contained" color="success"
              size="small"
              onClick={() => navigate(`/cases/${lead.case_id}`)}
              sx={{ textTransform: 'none' }}
            >
              Open Case
            </Button>
          ) : (
            <Button
              variant="contained" color="success"
              startIcon={<PursueIcon />}
              onClick={() => setPromoteOpen(true)}
              size="small" sx={{ textTransform: 'none' }}
            >
              Promote to Case
            </Button>
          )}
        </Box>
      </Box>

      <Box sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, maxWidth: 920 }}>
          {/* Provider context */}
          {(provider.display_name || provider.primary_taxonomy_code || provider.practice_state) && (
            <Section title="Provider context (NPPES)">
              <Box sx={{ display: 'grid', gridTemplateColumns: '160px 1fr', rowGap: 0.5, columnGap: 2 }}>
                {provider.display_name && <>
                  <Typography variant="body2" color="text.secondary">Name</Typography>
                  <Typography variant="body2">{provider.display_name}</Typography>
                </>}
                {provider.entity_type && <>
                  <Typography variant="body2" color="text.secondary">Entity</Typography>
                  <Typography variant="body2">{provider.entity_type}</Typography>
                </>}
                {provider.primary_taxonomy_code && <>
                  <Typography variant="body2" color="text.secondary">Taxonomy</Typography>
                  <Typography variant="body2">{provider.primary_taxonomy_code}</Typography>
                </>}
                {(provider.practice_city || provider.practice_state) && <>
                  <Typography variant="body2" color="text.secondary">Practice</Typography>
                  <Typography variant="body2">{[provider.practice_city, provider.practice_state].filter(Boolean).join(', ')}</Typography>
                </>}
                {provider.npi_is_active !== undefined && <>
                  <Typography variant="body2" color="text.secondary">NPI active</Typography>
                  <Typography variant="body2">{provider.npi_is_active ? 'yes' : 'no'}</Typography>
                </>}
              </Box>
            </Section>
          )}

          {/* Existing cases hint */}
          {lead.existing_cases_for_subject?.length > 0 && (
            <Section title="Already on cases">
              {lead.existing_cases_for_subject.map((c) => (
                <Paper key={c.case_id} variant="outlined" sx={{ p: 1.5, mb: 1, cursor: 'pointer' }} onClick={() => navigate(`/cases/${c.case_id}`)}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{c.case_name}</Typography>
                  <Typography variant="caption" color="text.secondary">{c.case_status}</Typography>
                </Paper>
              ))}
            </Section>
          )}

          {/* Notes */}
          <Section title="Notes">
            {lead.notes ? (
              <Paper variant="outlined" sx={{ p: 2, whiteSpace: 'pre-wrap', fontFamily: 'inherit' }}>
                <Typography variant="body2">{lead.notes}</Typography>
              </Paper>
            ) : (
              <Typography variant="body2" color="text.secondary">No notes yet.</Typography>
            )}
            {lead.disposition_reason && (
              <Box sx={{ mt: 1 }}>
                <Typography variant="caption" color="text.secondary">Disposition reason:</Typography>
                <Typography variant="body2">{lead.disposition_reason}</Typography>
              </Box>
            )}
          </Section>

          {/* Fire history */}
          <Section title={`Fire history (${lead.fires?.length || 0})`}>
            {lead.fires?.length === 0 ? (
              <Typography color="text.secondary" variant="body2">No fires recorded.</Typography>
            ) : (
              lead.fires.map((f) => (
                <Paper key={f.fire_id} variant="outlined" sx={{ p: 2, mb: 1.5 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {f.rule_id} v{f.rule_version}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {new Date(f.fired_at).toLocaleString()} · scan{' '}
                        <Link onClick={() => navigate(`/explore/scans/${f.scan_id}`)} sx={{ cursor: 'pointer' }}>
                          {f.scan_id.slice(0, 8)}…
                        </Link>
                      </Typography>
                    </Box>
                    <Box sx={{ textAlign: 'right' }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        ${parseFloat(f.amount_usd || '0').toLocaleString(undefined, { maximumFractionDigits: 0 })}
                      </Typography>
                      {f.metric_value && (
                        <Typography variant="caption" color="text.secondary">
                          metric: {f.metric_value}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                  {f.analysis_json && Object.keys(f.analysis_json).length > 0 && (
                    <>
                      <Divider sx={{ my: 1 }} />
                      <Box sx={{ display: 'grid', gridTemplateColumns: '160px 1fr', rowGap: 0.25, columnGap: 2 }}>
                        {Object.entries(f.analysis_json)
                          .filter(([k]) => !['display_name', 'entity_type', 'primary_taxonomy_code', 'practice_city', 'practice_state', 'npi_is_active'].includes(k))
                          .map(([k, v]) => (
                            <>
                              <Typography key={`k-${k}`} variant="caption" color="text.secondary">{k}</Typography>
                              <Typography key={`v-${k}`} variant="caption" sx={{ fontFamily: 'monospace' }}>{String(v)}</Typography>
                            </>
                          ))}
                      </Box>
                    </>
                  )}
                </Paper>
              ))
            )}
          </Section>
        </Box>
      </Box>

      {/* Note dialog */}
      <NoteDialog
        open={noteOpen}
        onClose={() => setNoteOpen(false)}
        leadId={lead.lead_id}
        onSaved={fetchLead}
      />

      {/* Status change dialog */}
      <StatusDialog
        open={statusDialog.open}
        targetStatus={statusDialog.target}
        onClose={() => setStatusDialog({ open: false, target: '' })}
        leadId={lead.lead_id}
        onSaved={fetchLead}
      />

      {/* Promote to Case */}
      <PromoteDialog
        open={promoteOpen}
        onClose={() => setPromoteOpen(false)}
        lead={lead}
        onPromoted={(caseId) => { setPromoteOpen(false); navigate(`/cases/${caseId}`); }}
      />
    </Box>
  );
};

// ===== Dialogs =====

export const NoteDialog = ({ open, onClose, leadId, onSaved }: { open: boolean; onClose: () => void; leadId: string; onSaved: () => void }) => {
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const submit = async () => {
    if (!note.trim()) { setErr('note is required'); return; }
    setSubmitting(true); setErr(null);
    try {
      await api.post(`/explore/leads/${leadId}/note/`, { note });
      onSaved();
      setNote('');
      onClose();
    } catch (e: any) {
      setErr(e?.response?.data?.error || e.message || 'failed');
    } finally { setSubmitting(false); }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Add note</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          fullWidth multiline minRows={3}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="What did you find?"
        />
        {err && <Alert severity="error" sx={{ mt: 1 }}>{err}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} sx={{ textTransform: 'none' }}>Cancel</Button>
        <Button variant="contained" onClick={submit} disabled={submitting} sx={{ textTransform: 'none' }}>
          {submitting ? 'Saving…' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export const StatusDialog = ({ open, targetStatus, onClose, leadId, onSaved }: {
  open: boolean; targetStatus: string; onClose: () => void; leadId: string; onSaved: () => void;
}) => {
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const submit = async () => {
    if (targetStatus !== 'open' && !reason.trim()) {
      setErr('reason is required when changing status'); return;
    }
    setSubmitting(true); setErr(null);
    try {
      await api.post(`/explore/leads/${leadId}/status/`, { status: targetStatus, reason });
      onSaved();
      setReason('');
      onClose();
    } catch (e: any) {
      setErr(e?.response?.data?.error || e.message || 'failed');
    } finally { setSubmitting(false); }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Set status: {targetStatus}</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          fullWidth multiline minRows={2}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Why? (required for non-open status changes)"
        />
        {err && <Alert severity="error" sx={{ mt: 1 }}>{err}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} sx={{ textTransform: 'none' }}>Cancel</Button>
        <Button variant="contained" onClick={submit} disabled={submitting} sx={{ textTransform: 'none' }}>
          {submitting ? 'Saving…' : 'Confirm'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export const PromoteDialog = ({ open, onClose, lead, onPromoted }: {
  open: boolean; onClose: () => void; lead: Lead; onPromoted: (caseId: string) => void;
}) => {
  const defaultName = lead.display_name
    ? `${lead.display_name} — fp lead ${lead.lead_id.slice(0, 8)}`
    : `${lead.subject_type}:${lead.subject_id} — fp lead ${lead.lead_id.slice(0, 8)}`;
  const [caseName, setCaseName] = useState(defaultName);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // Reset to default when dialog reopens with a different lead
  useEffect(() => { if (open) setCaseName(defaultName); /* eslint-disable-next-line */ }, [open, lead.lead_id]);

  const submit = async () => {
    if (!caseName.trim()) { setErr('Case name is required.'); return; }
    setSubmitting(true); setErr(null);
    try {
      const res = await api.post(`/explore/leads/${lead.lead_id}/promote/`, { case_name: caseName });
      onPromoted(res.data.case_id);
    } catch (e: any) {
      setErr(e?.response?.data?.error || e.message || 'failed');
    } finally { setSubmitting(false); }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Promote to Case</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Creates a new Case linked to this lead. The case starts with a log entry
          summarizing the lead's provenance — rule version, every fire, all triage notes.
          After promotion, case work continues in <strong>/cases/&lt;id&gt;</strong>.
        </Typography>
        <TextField
          autoFocus
          fullWidth
          label="Case name"
          value={caseName}
          onChange={(e) => setCaseName(e.target.value)}
        />
        {err && <Alert severity="error" sx={{ mt: 1 }}>{err}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting} sx={{ textTransform: 'none' }}>Cancel</Button>
        <Button
          variant="contained" color="success"
          onClick={submit}
          disabled={submitting}
          startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : undefined}
          sx={{ textTransform: 'none' }}
        >
          {submitting ? 'Creating case…' : 'Promote'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ===== Helpers =====

const Section = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <Box>
    <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mb: 1 }}>{title}</Typography>
    {children}
  </Box>
);

function leadStatusColor(s: string): 'default' | 'primary' | 'success' | 'warning' | 'error' {
  switch (s) {
    case 'open': return 'primary';
    case 'pursued': return 'success';
    case 'closed': return 'default';
    case 'dropped': return 'error';
    default: return 'default';
  }
}

export default LeadDetail;
